import {
  IconButton,
  Modal,
  PlusIcon,
  SingleSelectDropdown,
  Text,
  TextInput,
  toast,
} from "@carbon6/c6-frontend-design";
import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import {
  addMemberWorkspace,
  createTrainingWorkspace,
  isWorkspaceAlreadyClaimedError,
  listMemberWorkspaces,
  listTrainingGroups,
  listTrainingWorkspaceCandidates,
  renameTrainingWorkspace,
  removeMemberWorkspace,
  type MemberWorkspace,
  type OrgMemberWorkspaceRef,
  type RegistryGroupRef,
  type TrainingGroup,
  type TrainingWorkspaceCandidate,
} from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import styles from "./memberMappingModal.module.css";

export type WorkspaceMappingModalProps = {
  // 가입자 전용(memberUuid 가 null 이 아님). github_login·memberUuid 만 쓴다.
  memberUuid: string;
  githubLogin: string;
  // 대상 member 의 현재 소속 그룹(디렉터리 행). 생성 드롭다운을 이 소속으로 제한한다.
  memberGroups: readonly RegistryGroupRef[];
  onClose: () => void;
  // 매핑/표시명 변이가 성공할 때마다 다음 목록을 전달한다. 페이지가 표의 행을 즉시 교체한다.
  // 닫을 때가 아니라 변이마다 호출한다. 모달이 열려 있어도 뒤의 표가 맞아야 하기 때문이다.
  onChanged: (workspaces: readonly OrgMemberWorkspaceRef[]) => void;
};

// 표 셀은 스냅샷 2필드만 읽는다. 모달 로컬의 `created_at` 은 전달하지 않는다.
function toWorkspaceRefs(workspaces: readonly MemberWorkspace[]): OrgMemberWorkspaceRef[] {
  return workspaces.map((entry) => ({
    training_workspace_id: entry.training_workspace_id,
    workspace_name: entry.workspace_name,
  }));
}

// 한 가입자의 training workspace 매핑 편집(개인 me 스코프). 디렉터리에는 워크스페이스가 없다.
// 그래서 모달을 열 때 lazy 로 현재 매핑·후보를 받는다. 생성 요청은 training-api 에 보낸다.
// backend 는 registry 표시명을 따로 저장한다. 그래서 UI rename 은 training rename 지원 여부와
// 관계없다.
export function WorkspaceMappingModal({
  memberUuid,
  githubLogin,
  memberGroups,
  onClose,
  onChanged,
}: WorkspaceMappingModalProps): ReactNode {
  const [workspaces, setWorkspaces] = useState<MemberWorkspace[]>([]);
  const [candidates, setCandidates] = useState<TrainingWorkspaceCandidate[]>([]);
  const [mappedGroups, setMappedGroups] = useState<TrainingGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // 후보 조회 실패는 따로 안내한다. 그래서 현재 매핑 표시가 막히지 않는다(빈 목록과 실패를 구분).
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [groupCandidateError, setGroupCandidateError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createGroupId, setCreateGroupId] = useState<string | null>(null);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    setCandidateError(null);
    setGroupCandidateError(null);
    // 현재 매핑 + registry 매핑 팀을 먼저 받는다. workspace 후보는 member 소속 그룹으로만
    // fan-out 한다. 소속 밖 팀의 workspace 를 매핑하면 제출 스코프 밖(403)이다. 그래서 후보에서
    // 제외한다. 후보 프록시(training) 실패는 현재 매핑 표시를 막지 않도록 분리한다.
    void Promise.allSettled([listMemberWorkspaces(memberUuid), listTrainingGroups()]).then(
      async ([mapped, groupResults]) => {
        if (!alive) return;
        if (mapped.status === "fulfilled") {
          setWorkspaces(mapped.value);
        } else {
          setLoadError(resolveErrorMessage(mapped.reason));
        }
        if (groupResults.status === "fulfilled") {
          setMappedGroups(groupResults.value);
        } else {
          setGroupCandidateError(resolveErrorMessage(groupResults.reason));
        }
        const memberUuids = new Set(memberGroups.map((group) => group.group_uuid));
        const memberGroupIds =
          groupResults.status === "fulfilled"
            ? [
                ...new Set(
                  groupResults.value
                    .filter(
                      (group) => group.training_group_id !== null && memberUuids.has(group.uuid),
                    )
                    .map((group) => group.training_group_id as string),
                ),
              ]
            : [];
        const perGroup = await Promise.allSettled(
          memberGroupIds.map((groupId) => listTrainingWorkspaceCandidates(groupId)),
        );
        if (!alive) return;
        const merged = new Map<string, TrainingWorkspaceCandidate>();
        for (const result of perGroup) {
          if (result.status === "fulfilled") {
            for (const candidate of result.value) {
              if (!merged.has(candidate.id)) merged.set(candidate.id, candidate);
            }
          }
        }
        setCandidates([...merged.values()]);
        const firstFailure = perGroup.find(
          (result): result is PromiseRejectedResult => result.status === "rejected",
        );
        if (firstFailure !== undefined) {
          setCandidateError(resolveErrorMessage(firstFailure.reason));
        }
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [memberUuid, memberGroups, reload]);

  const mapped = new Set(workspaces.map((entry) => entry.training_workspace_id));
  const addOptions = candidates
    .filter((candidate) => !mapped.has(candidate.id))
    .map((candidate) => ({ value: candidate.id, label: candidate.name }));
  // 생성 대상은 "이 member 가 소속된 그룹" ∩ "training 그룹이 연결된 팀"뿐이다. 소속 밖 그룹에
  // 만들면 그 member 의 제출 스코프에 포함되지 않아 403 이 된다(미매핑 그룹은 BE 도 422 로 거부).
  // 옵션 값은 POST group_id 로 보낼 training 그룹 id 다. 라벨은 팀 이름이다. 같은 그룹을
  // 가리키는 팀이 여럿이면 첫 팀만 남긴다.
  const memberGroupUuids = new Set(memberGroups.map((group) => group.group_uuid));
  const groupOptionMap = new Map<string, string>();
  for (const group of mappedGroups) {
    if (
      group.training_group_id !== null &&
      memberGroupUuids.has(group.uuid) &&
      !groupOptionMap.has(group.training_group_id)
    ) {
      groupOptionMap.set(group.training_group_id, group.name);
    }
  }
  const groupOptions = [...groupOptionMap].map(([value, label]) => ({ value, label }));

  function handleOpenCreateModal(): void {
    if (busy || groupOptions.length === 0) return;
    setCreateModalOpen(true);
  }

  function handleCloseCreateModal(): void {
    if (busy) return;
    setCreateModalOpen(false);
    setNewWorkspaceName("");
    // 다음에 열 때 그룹을 다시 명시적으로 고르게 한다. 이전 선택이 알림 없이 이어지지 않게 한다.
    setCreateGroupId(null);
  }

  async function handleAddSelected(): Promise<void> {
    if (selectedWorkspaceId === null) return;
    const candidate = candidates.find((entry) => entry.id === selectedWorkspaceId);
    if (!candidate || busy) return;
    setBusy(true);
    try {
      const added = await addMemberWorkspace(memberUuid, {
        training_workspace_id: candidate.id,
        workspace_name: candidate.name,
      });
      const next = [...workspaces, added];
      setWorkspaces(next);
      onChanged(toWorkspaceRefs(next));
      setSelectedWorkspaceId(null);
    } catch (error) {
      toast.error(
        isWorkspaceAlreadyClaimedError(error)
          ? "이미 다른 멤버가 선점한 Workspace입니다."
          : resolveErrorMessage(error),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(workspaceId: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      await removeMemberWorkspace(memberUuid, workspaceId);
      const next = workspaces.filter((entry) => entry.training_workspace_id !== workspaceId);
      setWorkspaces(next);
      onChanged(toWorkspaceRefs(next));
    } catch (error) {
      toast.error(resolveErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateAndAdd(): Promise<void> {
    const name = newWorkspaceName.trim();
    if (busy || createGroupId === null || name === "") return;
    setBusy(true);
    try {
      const created = await createTrainingWorkspace({ group_id: createGroupId, name });
      setCandidates((prev) => upsertCandidate(prev, created));
      const added = await addMemberWorkspace(memberUuid, {
        training_workspace_id: created.id,
        workspace_name: created.name,
      });
      const next = [...workspaces, added];
      setWorkspaces(next);
      onChanged(toWorkspaceRefs(next));
      setNewWorkspaceName("");
      setCreateGroupId(null);
      setCreateModalOpen(false);
    } catch (error) {
      toast.error(resolveErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function handleStartRename(workspace: MemberWorkspace): void {
    if (busy) return;
    setRenamingId(workspace.training_workspace_id);
    setRenameName(workspace.workspace_name);
  }

  function handleCancelRename(): void {
    if (busy) return;
    setRenamingId(null);
    setRenameName("");
  }

  async function handleRename(workspace: MemberWorkspace): Promise<void> {
    const name = renameName.trim();
    if (busy || name === "") return;
    if (name === workspace.workspace_name) {
      handleCancelRename();
      return;
    }
    setBusy(true);
    try {
      const renamed = await renameTrainingWorkspace(workspace.training_workspace_id, { name });
      setCandidates((prev) => upsertCandidate(prev, renamed));
      const next = workspaces.map((entry) =>
        entry.training_workspace_id === renamed.id
          ? { ...entry, workspace_name: renamed.name }
          : entry,
      );
      setWorkspaces(next);
      onChanged(toWorkspaceRefs(next));
      setRenamingId(null);
      setRenameName("");
      toast.success("Workspace 이름을 변경했습니다.");
    } catch (error) {
      toast.error(resolveErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      closeOnBackdropClick={!busy && !createModalOpen}
      closeOnEscape={!busy && !createModalOpen}
      aria-labelledby="workspace-mapping-title"
    >
      <HatModalHeader
        title="Training Workspace를 매핑합니다."
        titleId="workspace-mapping-title"
        close={{ disabled: busy }}
      />
      <div className={styles.body}>
        <Text variant={hatcheryTextModes["caption-bold"]} className={styles.subjectName}>
          {githubLogin}
        </Text>
        {loading ? (
          <LoadingState>Workspace를 불러오는 중입니다.</LoadingState>
        ) : loadError ? (
          <EmptyState
            tone="error"
            action={{ label: "다시 시도", onClick: () => setReload((value) => value + 1) }}
          >
            {loadError}
          </EmptyState>
        ) : (
          <>
            <section className={styles.selectionPanel} aria-label="Workspace 선택">
              {candidateError ? (
                <EmptyState size="sm">
                  Workspace 후보를 모두 불러오지 못했습니다. 현재 매핑은 그대로 편집할 수 있습니다.
                </EmptyState>
              ) : null}
              <div className={styles.selectionRow}>
                <SingleSelectDropdown
                  mode={hatcheryDropdownModes.default}
                  className={styles.workspaceSelect}
                  label="Workspace"
                  value={selectedWorkspaceId}
                  options={addOptions}
                  placeholder="Workspace를 선택해 주세요."
                  emptyMessage={
                    // 후보를 받지 못했으면 "없습니다" 로 알리지 않는다.
                    candidateError !== null || groupCandidateError !== null
                      ? "Workspace 후보를 불러오지 못했습니다. 창을 닫았다가 다시 열어 주세요."
                      : "추가할 Workspace가 없습니다."
                  }
                  noResultsMessage="검색 결과가 없습니다."
                  searchPlaceholder="Workspace를 검색해 보세요."
                  searchAction={
                    <IconButton
                      text="새 Workspace 추가"
                      variant="surface"
                      size="sm"
                      disabled={busy || groupOptions.length === 0}
                      onClick={handleOpenCreateModal}
                    >
                      <PlusIcon />
                    </IconButton>
                  }
                  searchable
                  menuWidth="min(32rem, calc(100vw - 2rem))"
                  disabled={busy || (addOptions.length === 0 && groupOptions.length === 0)}
                  onValueChange={setSelectedWorkspaceId}
                />
                <IconButton
                  text="선택 추가"
                  variant="solid"
                  disabled={busy || selectedWorkspaceId === null}
                  onClick={() => void handleAddSelected()}
                />
              </div>
              {groupCandidateError ? (
                <EmptyState size="sm">
                  그룹 목록을 불러오지 못해 Workspace 를 추가할 수 없습니다. 현재 매핑은 그대로
                  편집할 수 있습니다.
                </EmptyState>
              ) : null}
            </section>
            <div className={styles.items} aria-label="현재 매핑">
              {workspaces.length === 0 ? (
                <EmptyState size="sm">아직 매핑된 Workspace가 없습니다.</EmptyState>
              ) : (
                workspaces.map((workspace) => (
                  <span key={workspace.training_workspace_id} className={styles.item}>
                    {renamingId === workspace.training_workspace_id ? (
                      <span className={styles.renameRow}>
                        <TextInput
                          value={renameName}
                          aria-label={`${workspace.workspace_name} 새 이름`}
                          disabled={busy}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setRenameName(event.target.value)
                          }
                        />
                        <IconButton
                          text="저장"
                          variant="solid"
                          size="sm"
                          disabled={busy || renameName.trim() === ""}
                          onClick={() => void handleRename(workspace)}
                        />
                        <IconButton
                          text="취소"
                          variant="surface"
                          size="sm"
                          disabled={busy}
                          onClick={handleCancelRename}
                        />
                      </span>
                    ) : (
                      <>
                        <Text variant={hatcheryTextModes["body-normal"]}>
                          {workspace.workspace_name}
                        </Text>
                        <span className={styles.itemActions}>
                          <IconButton
                            text="이름 변경"
                            variant="surface"
                            size="sm"
                            disabled={busy}
                            onClick={() => handleStartRename(workspace)}
                          />
                          <IconButton
                            text="제거"
                            variant="surface"
                            size="sm"
                            tone="danger"
                            disabled={busy}
                            onClick={() => void handleRemove(workspace.training_workspace_id)}
                          />
                        </span>
                      </>
                    )}
                  </span>
                ))
              )}
            </div>
          </>
        )}
      </div>
      {createModalOpen ? (
        <CreateWorkspaceModal
          busy={busy}
          createGroupId={createGroupId}
          groupCandidateError={groupCandidateError}
          groupOptions={groupOptions}
          newWorkspaceName={newWorkspaceName}
          onClose={handleCloseCreateModal}
          onCreate={() => void handleCreateAndAdd()}
          onGroupChange={setCreateGroupId}
          onWorkspaceNameChange={setNewWorkspaceName}
        />
      ) : null}
    </Modal>
  );
}

type CreateWorkspaceModalProps = {
  busy: boolean;
  createGroupId: string | null;
  groupCandidateError: string | null;
  groupOptions: { value: string; label: string }[];
  newWorkspaceName: string;
  onClose: () => void;
  onCreate: () => void;
  onGroupChange: (value: string) => void;
  onWorkspaceNameChange: (value: string) => void;
};

function CreateWorkspaceModal({
  busy,
  createGroupId,
  groupCandidateError,
  groupOptions,
  newWorkspaceName,
  onClose,
  onCreate,
  onGroupChange,
  onWorkspaceNameChange,
}: CreateWorkspaceModalProps): ReactNode {
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      level="top"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      aria-labelledby="workspace-create-title"
    >
      <HatModalHeader
        title="새 Workspace를 만듭니다."
        titleId="workspace-create-title"
        close={{ disabled: busy }}
      />
      <div className={styles.createModalBody}>
        {groupCandidateError ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.muted} role="alert">
            {groupCandidateError}
          </Text>
        ) : null}
        <div className={styles.createModalFields}>
          <SingleSelectDropdown
            mode={hatcheryDropdownModes.default}
            label="그룹"
            value={createGroupId}
            options={groupOptions}
            placeholder={
              groupOptions.length === 0
                ? "소속 그룹이 없습니다. 먼저 그룹을 매핑해 주세요."
                : "그룹을 선택해 주세요."
            }
            disabled={busy || groupOptions.length === 0}
            onValueChange={onGroupChange}
          />
          <TextInput
            value={newWorkspaceName}
            placeholder="Workspace 이름을 입력해 주세요."
            aria-label="Workspace 이름"
            disabled={busy || groupOptions.length === 0}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onWorkspaceNameChange(event.target.value)
            }
          />
          <IconButton
            text="생성 후 추가"
            variant="solid"
            disabled={
              busy ||
              createGroupId === null ||
              groupOptions.length === 0 ||
              newWorkspaceName.trim() === ""
            }
            onClick={onCreate}
          />
        </div>
      </div>
    </Modal>
  );
}

function upsertCandidate(
  candidates: TrainingWorkspaceCandidate[],
  candidate: TrainingWorkspaceCandidate,
): TrainingWorkspaceCandidate[] {
  const exists = candidates.some((entry) => entry.id === candidate.id);
  if (!exists) {
    return [...candidates, candidate];
  }
  return candidates.map((entry) => (entry.id === candidate.id ? candidate : entry));
}

function resolveErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Workspace 매핑 요청을 완료하지 못했습니다.";
}
